import { useState } from 'react'
import {
  ChevronRightIcon,
  FolderIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import type { MaterialTreeNode } from '../../services/sampleProjectApi'

interface MaterialFolderTreeProps {
  nodes: MaterialTreeNode[]
  selectedId?: number | null
  onSelectFile: (node: MaterialTreeNode) => void
  onSelectFolder?: (node: MaterialTreeNode) => void
  onDownloadFolder?: (node: MaterialTreeNode) => void
  emptyMessage?: string
}

const TreeItem = ({
  node,
  depth,
  selectedId,
  onSelectFile,
  onSelectFolder,
  onDownloadFolder,
}: {
  node: MaterialTreeNode
  depth: number
  selectedId?: number | null
  onSelectFile: (node: MaterialTreeNode) => void
  onSelectFolder?: (node: MaterialTreeNode) => void
  onDownloadFolder?: (node: MaterialTreeNode) => void
}) => {
  const [open, setOpen] = useState(depth < 2)
  const selected = selectedId === node.id

  if (node.nodeType === 'FOLDER') {
    return (
      <div>
        <div
          className={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-sm hover:bg-m2-surface-high ${
            selected ? 'bg-m2-primary/15' : ''
          }`}
          style={{ paddingLeft: 8 + depth * 12 }}
        >
          <button
            type="button"
            className={`flex min-w-0 flex-1 items-center gap-1 text-left ${
              selected ? 'text-m2-primary' : 'text-m2-on-surface'
            }`}
            onClick={() => {
              setOpen((v) => !v)
              onSelectFolder?.(node)
            }}
          >
            <ChevronRightIcon
              className={`h-4 w-4 shrink-0 text-m2-on-surface-variant transition-transform ${
                open ? 'rotate-90' : ''
              }`}
            />
            <FolderIcon className="h-4 w-4 shrink-0 text-amber-600" />
            <span className="truncate font-medium">{node.name}</span>
          </button>
          {onDownloadFolder && (
            <button
              type="button"
              title="Tải thư mục này"
              className="shrink-0 rounded p-1 text-m2-on-surface-variant opacity-0 hover:bg-m2-surface-container hover:text-m2-primary group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation()
                onDownloadFolder(node)
              }}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {open &&
          node.children.map((child) => (
            <TreeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelectFile={onSelectFile}
              onSelectFolder={onSelectFolder}
              onDownloadFolder={onDownloadFolder}
            />
          ))}
      </div>
    )
  }

  return (
    <button
      type="button"
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${
        selected
          ? 'bg-m2-primary/15 text-m2-primary'
          : 'text-m2-on-surface hover:bg-m2-surface-high'
      }`}
      style={{ paddingLeft: 8 + depth * 12 + 20 }}
      onClick={() => onSelectFile(node)}
    >
      <DocumentIcon className="h-4 w-4 shrink-0" />
      <span className="truncate">{node.name}</span>
      {node.fileType && (
        <span className="ml-auto shrink-0 text-[10px] uppercase text-m2-on-surface-variant">
          {node.fileType}
        </span>
      )}
    </button>
  )
}

const MaterialFolderTree = ({
  nodes,
  selectedId,
  onSelectFile,
  onSelectFolder,
  onDownloadFolder,
  emptyMessage = 'Chưa có file trong dự án mẫu này',
}: MaterialFolderTreeProps) => {
  if (!nodes.length) {
    return (
      <p className="px-3 py-6 text-center text-sm text-m2-on-surface-variant">{emptyMessage}</p>
    )
  }

  return (
    <div className="py-1">
      {nodes.map((node) => (
        <TreeItem
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          onSelectFile={onSelectFile}
          onSelectFolder={onSelectFolder}
          onDownloadFolder={onDownloadFolder}
        />
      ))}
    </div>
  )
}

export default MaterialFolderTree
